import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Flame, TrendingUp, ExternalLink, RefreshCw, Info } from 'lucide-react';
import { Card, CardTitle, CardDescription, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { formatNumber } from '@keywordgod/shared';
import { ScoreBadge } from '@/components/keyword';

/**
 * Phase 43: 내일 핫해질 키워드 예측
 * DataLab 14일 데이터로 last 7d vs prior 7d 가속도 계산.
 * 6h KV 캐시.
 */
export function Trending() {
  const q = useQuery({
    queryKey: ['trending', 'tomorrow'],
    queryFn: () => api.trending.tomorrow(),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Flame className="h-7 w-7 text-orange-400" />
          <span className="text-gold-gradient">내일 핫해질 키워드</span>
          <span className="ml-2 rounded-full bg-orange-400/20 px-2 py-0.5 text-[11px] font-bold text-orange-200">
            BETA
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          최근 7일 vs 이전 7일 검색량 가속도가 가장 큰 키워드 — 지금 글 쓰면 검색 피크 직전에 색인됩니다.
        </p>
      </header>

      <Card className="border-orange-400/30 bg-orange-400/5">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-orange-300 mt-0.5" />
          <div className="text-xs text-orange-100/80 space-y-1">
            <p>
              <strong>알고리즘:</strong> 분석된 키워드 중 검색량 200+ 후보 80개를 매 6시간마다 DataLab 14일
              호출 → "최근 7일 평균이 이전 7일 평균 대비 15% 이상 상승" + "최근 평균 ratio ≥ 5" 인 것만 추출.
            </p>
            <p>
              <strong>이 리스트의 한계:</strong> 우리가 이미 분석한 키워드 풀 내에서만 발견합니다. 완전 신규
              유행어는 분석 데이터가 없어 못 잡습니다. → "키워드 발굴" 메뉴와 병행 사용 추천.
            </p>
          </div>
        </div>
      </Card>

      {q.isLoading && (
        <Card>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
          <p className="mt-4 text-xs text-gray-500">
            <RefreshCw className="inline h-3 w-3 mr-1 animate-spin" />
            DataLab 호출 중 — 첫 계산 시 30~60초 걸릴 수 있어요. (이후 6시간 캐시)
          </p>
        </Card>
      )}

      {q.isError && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-200 text-sm">
          핫 키워드 분석 실패: {(q.error as Error).message}
        </Card>
      )}

      {q.data && (
        <>
          <Card>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-orange-400" /> 상승 가속도 TOP {q.data.items.length}
              </CardTitle>
              <span className="text-xs text-gray-500">
                후보 {q.data.scannedCandidates}개 스캔 · 갱신:{' '}
                {new Date(q.data.computedAt * 1000).toLocaleString('ko-KR')}
              </span>
            </div>
            <CardDescription>
              가속도(acc)는 (최근 7일 평균 ÷ 이전 7일 평균) - 1. +0.5 면 50% 상승. 표시값은 acc × 최근 평균 ratio
              가중 정렬.
            </CardDescription>

            {q.data.items.length === 0 ? (
              <p className="mt-4 text-sm text-gray-400">
                현재 풀에서 명확한 상승 가속도가 잡힌 키워드가 없습니다. 6시간 뒤 다시 확인해주세요.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                      <th className="py-2 pr-2 font-medium w-10 text-right">#</th>
                      <th className="py-2 pr-2 font-medium">키워드</th>
                      <th className="py-2 pr-2 font-medium text-right">가속도</th>
                      <th className="py-2 pr-2 font-medium text-right">최근/이전 평균</th>
                      <th className="py-2 pr-2 font-medium text-right">월검색</th>
                      <th className="py-2 pr-2 font-medium text-right">포화도</th>
                      <th className="py-2 font-medium text-right">점수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.items.map((it, i) => {
                      const pct = Math.round(it.acc * 100);
                      const accColor =
                        it.acc >= 0.5 ? 'text-red-300' : it.acc >= 0.3 ? 'text-orange-300' : 'text-amber-300';
                      return (
                        <tr key={it.keyword} className="border-b border-gray-900 last:border-b-0 hover:bg-gray-950/60">
                          <td className="py-2 pr-2 text-right tabular text-xs text-gray-500">{i + 1}</td>
                          <td className="py-2 pr-2">
                            <Link
                              to={`/analyze/${encodeURIComponent(it.keyword)}`}
                              className="font-medium text-white hover:text-gold"
                            >
                              {it.keyword}
                              <ExternalLink className="inline ml-1 h-3 w-3 text-gray-600" />
                            </Link>
                          </td>
                          <td className={`py-2 pr-2 text-right tabular font-bold ${accColor}`}>
                            {pct >= 0 ? '+' : ''}
                            {pct}%
                          </td>
                          <td className="py-2 pr-2 text-right tabular text-xs text-gray-400">
                            {it.recent7Avg.toFixed(1)} / {it.prior7Avg.toFixed(1)}
                          </td>
                          <td className="py-2 pr-2 text-right tabular text-xs text-gray-300">
                            {formatNumber(it.totalSearch)}
                          </td>
                          <td className="py-2 pr-2 text-right tabular text-xs text-gray-400">
                            {it.saturation.toFixed(0)}
                          </td>
                          <td className="py-2 text-right">
                            <div className="inline-flex items-center gap-1.5">
                              <span className="tabular text-sm text-gold">{it.godScore}</span>
                              <ScoreBadge grade={it.grade as 'S' | 'A' | 'B' | 'C' | 'D'} size="sm" />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <p className="text-xs text-gray-600 text-center">
            다음 갱신: {new Date(q.data.nextRefreshAt * 1000).toLocaleString('ko-KR')}
          </p>
        </>
      )}
    </div>
  );
}
